/**
 * SampleQcChecklist — the per-round QC review (BLU-629).
 *
 * Jack's sampling checklist: measurement, colour, fabric, labels & trims, and
 * the physical label-and-file step. Each check carries a verdict (pass / fail /
 * n-a) and a note saying what specifically went right or wrong — the free-text
 * `fit_notes` field above it could never answer "which part failed?".
 *
 * Interaction mirrors the colourway status pill: the verdict button cycles
 * unset → pass → fail → n-a → unset and paints immediately (optimistically),
 * because a reviewer sets these many times per sample and an explicit Edit/Save
 * round-trip per check would be real friction. Notes save on blur via InlineEdit.
 *
 * The SAVE is debounced (`COMMIT_DELAY_MS`) rather than fired per click: reaching
 * "n-a" costs three clicks (pass → fail → n-a), and an immediate save per click
 * wrote all three to `activity_log` — the timeline then claimed a check FAILED
 * that never did. One burst of clicks is one intent, so it becomes one write and
 * one timeline entry. Every write is enqueued through the hook's serial queue
 * (mirrors `setVariantsSerialized`) so responses can't land out of order and
 * repaint a stale verdict.
 *
 * Collapsed by default: the header carries the answer to "what failed on this
 * round?" (a rose chip per failed check) so a card holding six rounds still
 * scans. Expanding is for doing the review or reading the notes.
 *
 * Deliberately inert: a failed check does NOT reject the round or move the
 * record. Recording the finding and acting on it are separate decisions, and the
 * operator still owns the second one.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Minus, X } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { InlineEdit } from './InlineEdit';
import {
  SAMPLE_QC_CHECKS,
  SAMPLE_QC_RESULTS,
  SAMPLE_QC_RESULT_CLASS,
  type SampleQcCheckKind,
  type SampleQcResult,
} from '../utils/status-style';
import { useProductDevMutations } from '../hooks/useProductDev';

type QcCheck = { result: SampleQcResult; note: string | null };
type QcChecks = Partial<Record<SampleQcCheckKind, QcCheck>>;

interface Props {
  productDevId: string;
  sampleRoundId: string;
  qcChecks: QcChecks;
  canEdit: boolean;
}

/**
 * Each verdict is DIRECTLY selectable — this deliberately replaced a
 * unset→pass→fail→na click-cycle, and the reason is not ergonomics, it's
 * honesty.
 *
 * With a cycle, recording "fail" means transiting THROUGH "pass". Every commit
 * writes an `activity_log` row, so a reviewer pausing mid-cycle (or simply
 * clicking at human speed) permanently logged `Colour pass` — a finding that
 * never happened — immediately before the real one. Caught live: two clicks a
 * few seconds apart produced BOTH "Colour pass" and "Colour fail" in the
 * timeline. Debouncing only hides that when the clicks happen to land close
 * enough together; it cannot fix it, because the intermediate state is a lie the
 * control forces the user to tell.
 *
 * Direct selection makes the wrong state unreachable by construction: one click,
 * one verdict, timing-independent. Clicking the ACTIVE verdict clears it back to
 * unset — a check ticked by mistake must be retractable to "not reviewed", which
 * is a distinct state from "not applicable".
 */
const RESULT_ICON = {
  pass: Check,
  fail: X,
  na: Minus,
} as const;

/** Long enough to absorb the click-through to "n-a", short enough that a single
 *  verdict feels saved by the time the eye leaves the row. Not a motion value —
 *  DESIGN.md §5 governs animation, not network coalescing. */
const COMMIT_DELAY_MS = 500;

export function SampleQcChecklist({
  productDevId,
  sampleRoundId,
  qcChecks,
  canEdit,
}: Props) {
  const { t } = useTranslation('productDev');
  const { setSampleQcSerialized, optimisticSampleQc } = useProductDevMutations(productDevId);

  const reviewed = SAMPLE_QC_CHECKS.filter((kind) => qcChecks[kind]);
  const failedKinds = SAMPLE_QC_CHECKS.filter((kind) => qcChecks[kind]?.result === 'fail');
  const passed = reviewed.filter((kind) => qcChecks[kind]?.result === 'pass').length;
  const remaining = SAMPLE_QC_CHECKS.length - reviewed.length;
  const hasReview = reviewed.length > 0;
  // Every check reviewed, none of them a verdict worth a count: without this the
  // rollup rendered an empty span and an all-n/a round read as "never reviewed".
  const allNotApplicable =
    hasReview && passed === 0 && failedKinds.length === 0 && remaining === 0;

  const [open, setOpen] = useState(false);
  // Announces the verdict a click just produced. A button's aria-label changing
  // under the cursor is not reliably spoken; a polite live region is.
  const [announcement, setAnnouncement] = useState('');
  const panelId = useId();

  // Debounced, serialized commit. `pendingRef` always holds the WHOLE checklist
  // (the mutation is a full replace), so a burst touching two different checks
  // still collapses into one correct payload.
  const pendingRef = useRef<QcChecks | null>(null);
  const timerRef = useRef<number | null>(null);

  const flush = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const next = pendingRef.current;
    if (!next) return;
    pendingRef.current = null;
    void setSampleQcSerialized(sampleRoundId, () => next).catch((err: unknown) => {
      toast({
        title: t('sampleQc.toast.saveFailed'),
        description: err instanceof Error ? err.message : t('samples.toast.tryAgain'),
        variant: 'destructive',
      });
    });
  }, [setSampleQcSerialized, sampleRoundId, t]);

  // Never strand a staged verdict: if the row unmounts (navigation, the round
  // being deleted, a refetch reordering the list) the pending write still goes.
  const flushRef = useRef(flush);
  flushRef.current = flush;
  useEffect(() => () => flushRef.current(), []);

  /** Paint now, write later (or now, for a committed note). */
  const stage = (next: QcChecks, immediate = false) => {
    optimisticSampleQc(sampleRoundId, next);
    pendingRef.current = next;
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = null;
    if (immediate) {
      flush();
      return;
    }
    timerRef.current = window.setTimeout(flush, COMMIT_DELAY_MS);
  };

  /** The freshest checklist, which is NOT always the prop. Reaching "n-a" is a
   *  three-click burst (unset → pass → fail → n-a), and clicks inside a single
   *  render frame would all read the same stale `qcChecks` and each compute the
   *  same "next" state — so a triple-click could land on `pass` instead of `na`.
   *  `pendingRef` holds the staged checklist the moment it's built, so it leads
   *  the prop; fall back to the prop once a write has flushed and cleared it. */
  const latestChecks = (): QcChecks => pendingRef.current ?? qcChecks;

  /** Set (or, if it's already the active verdict, clear) one check. */
  const selectResult = (kind: SampleQcCheckKind, result: SampleQcResult) => {
    const base = latestChecks();
    const isActive = base[kind]?.result === result;
    const draft: QcChecks = { ...base };
    if (isActive) {
      delete draft[kind];
    } else {
      draft[kind] = { result, note: base[kind]?.note ?? null };
    }
    setAnnouncement(
      t('sampleQc.announce', {
        check: t(`sampleQc.check.${kind}`),
        result: t(`sampleQc.result.${isActive ? 'unset' : result}`),
      }),
    );
    stage(draft);
  };

  const setNote = (kind: SampleQcCheckKind, note: string | null) => {
    // A note without a verdict is still worth keeping — default it to n-a
    // rather than dropping what the reviewer just typed.
    const base = latestChecks();
    const draft: QcChecks = {
      ...base,
      [kind]: { result: base[kind]?.result ?? 'na', note },
    };
    stage(draft, true);
  };

  if (!canEdit && !hasReview) return null;

  return (
    <div className="mt-2 rounded-md border border-border/70 bg-muted/30">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        // Only while the panel is mounted — aria-controls must point at a real
        // element, and the panel is conditionally rendered to keep the card light.
        aria-controls={open ? panelId : undefined}
        className={cn(
          'flex w-full flex-wrap items-center gap-x-2 gap-y-1 px-2.5 py-1.5 text-left',
          'text-xs text-muted-foreground hover:text-foreground',
          'transition-colors duration-200 motion-reduce:transition-none',
        )}
      >
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 shrink-0 transition-transform duration-200 motion-reduce:transition-none',
            !open && '-rotate-90',
          )}
          aria-hidden="true"
        />
        <span className="font-medium">{t('sampleQc.title')}</span>

        {hasReview ? (
          <span className="flex min-w-0 flex-wrap items-center gap-1.5">
            {/* The glance answer: WHICH checks failed, named, without expanding.
                Icon + word (sr-only) carry the meaning — never the colour alone. */}
            {failedKinds.map((kind) => (
              <span
                key={kind}
                className="inline-flex items-center gap-1 rounded-md bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-800 ring-1 ring-rose-200"
              >
                <X className="h-2.5 w-2.5" strokeWidth={3} aria-hidden="true" />
                {t(`sampleQc.check.${kind}`)}
                <span className="sr-only"> {t('sampleQc.result.fail')}</span>
              </span>
            ))}
            {/* `n`, not `count` — `count` would trigger i18next plural
                resolution, and zh has no plural forms. */}
            {passed > 0 && (
              <span className="text-emerald-700">{t('sampleQc.passedCount', { n: passed })}</span>
            )}
            {/* Honest rollup: "3 passed" alone would read as a finished review
                when two checks were never opened. */}
            {remaining > 0 && (
              <span className="text-muted-foreground">
                {t('sampleQc.remainingCount', { n: remaining })}
              </span>
            )}
            {allNotApplicable && (
              <span className="text-muted-foreground">{t('sampleQc.result.na')}</span>
            )}
          </span>
        ) : (
          <span className="italic">{t('sampleQc.notReviewed')}</span>
        )}
      </button>

      <span role="status" aria-live="polite" className="sr-only">
        {announcement}
      </span>

      {open && (
        <div
          id={panelId}
          className="space-y-0 border-t border-border/70 px-2.5 py-1 motion-safe:animate-fade-in"
        >
          {SAMPLE_QC_CHECKS.map((kind) => {
            const check = qcChecks[kind];
            const Icon = check ? RESULT_ICON[check.result] : null;
            const resultLabel = check
              ? t(`sampleQc.result.${check.result}`)
              : t('sampleQc.result.unset');
            const checkLabel = t(`sampleQc.check.${kind}`);
            return (
              <div
                key={kind}
                className="flex items-start gap-2 py-1.5 border-b border-border/50 last:border-b-0"
              >
                {/* Three directly-selectable verdicts, NOT a cycle — see the
                    RESULT_ICON docblock. Radiogroup semantics: exactly one may
                    be active, and it may also be none (unset = not reviewed).
                    Read-only renders just the chosen verdict, so a reviewer
                    without write access isn't shown three dead buttons. */}
                {canEdit ? (
                  <div
                    role="radiogroup"
                    aria-label={checkLabel}
                    className="flex shrink-0 items-center gap-0.5"
                  >
                    {SAMPLE_QC_RESULTS.map((result) => {
                      const ResultIcon = RESULT_ICON[result];
                      const active = check?.result === result;
                      return (
                        <button
                          key={result}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          onClick={() => selectResult(kind, result)}
                          title={t(`sampleQc.result.${result}`)}
                          aria-label={t('sampleQc.selectAria', {
                            check: checkLabel,
                            result: t(`sampleQc.result.${result}`),
                          })}
                          className={cn(
                            // 24px — WCAG 2.5.5 / DESIGN.md §16 minimum target.
                            'flex h-6 w-6 items-center justify-center rounded-md',
                            'transition-colors duration-200 motion-reduce:transition-none',
                            active
                              ? SAMPLE_QC_RESULT_CLASS[result]
                              : 'text-muted-foreground/35 hover:text-muted-foreground hover:bg-muted',
                            'hover:ring-2 hover:ring-threadheads-purple/30',
                          )}
                        >
                          <ResultIcon className="h-3 w-3" strokeWidth={3} aria-hidden="true" />
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <span
                    aria-label={t('sampleQc.announce', {
                      check: checkLabel,
                      result: resultLabel,
                    })}
                    className={cn(
                      'flex h-6 w-6 shrink-0 items-center justify-center rounded-md',
                      check
                        ? SAMPLE_QC_RESULT_CLASS[check.result]
                        : 'border border-dashed border-muted-foreground/40',
                    )}
                  >
                    {Icon && <Icon className="h-3 w-3" strokeWidth={3} aria-hidden="true" />}
                  </span>
                )}

                <span
                  className={cn(
                    'w-[104px] shrink-0 pt-1 text-xs',
                    check?.result === 'fail'
                      ? 'font-semibold text-foreground'
                      : 'text-muted-foreground',
                  )}
                >
                  {checkLabel}
                </span>

                {/* max-w-prose caps the measure on both the display and the
                    input (DESIGN.md §10) at wide viewports. */}
                <div className="min-w-0 max-w-prose flex-1">
                  <InlineEdit
                    value={check?.note ?? null}
                    onSave={(v) => setNote(kind, v)}
                    placeholder={t(`sampleQc.placeholder.${kind}`)}
                    readOnly={!canEdit}
                    displayClassName="text-xs text-muted-foreground"
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
